import { Collapse, Drawer, Empty, Tag } from 'antd'
import { diffTokens } from 'features/libra/llmDiff/diffTokens'
import { MISSING_VALUE_PLACEHOLDER } from 'features/libra/llmDiff/formatFieldValue'
import type { FC } from 'react'
import styled from 'styled-components'

import { AssetModelDiffRow } from './diffAssetModel'
import { useFfsAssetModelDiff } from './useFfsAssetModelDiff'

interface Props {
  onClose: () => void
}

/**
 * Gecko-internal drawer showing what a ticket's asset-model snapshot changed
 * relative to the unit's live master asset model. Mirrors `LlmDiffDrawer`'s
 * visual structure (summary line, grouped `Collapse`, 2-column value grid
 * with word-level highlighting) but diffs the FFS asset-model snapshot
 * instead of ontology field values.
 */
export const FfsAssetModelDiffDrawer: FC<Props> = ({ onClose }) => {
  const { groups, changeCount } = useFfsAssetModelDiff()

  return (
    <Drawer
      title='Asset model changes'
      placement='right'
      width={720}
      onClose={onClose}
      open
      styles={{ body: { padding: '1.25rem 1.5rem', overflowY: 'auto' } }}>
      {changeCount === 0 ? (
        <Empty description="No changes from the unit's asset model." />
      ) : (
        <>
          <SummaryLine>
            {changeCount} field{changeCount === 1 ? '' : 's'} changed across{' '}
            {groups.length} component{groups.length === 1 ? '' : 's'}
          </SummaryLine>
          <Collapse
            bordered={false}
            defaultActiveKey={groups.map((g) => g.componentId)}
            items={groups.map((g) => ({
              key: g.componentId,
              label: (
                <GroupHeader>
                  <GroupTitle>{g.componentName}</GroupTitle>
                  <GroupCount>
                    {g.rows.length} field{g.rows.length === 1 ? '' : 's'}
                  </GroupCount>
                </GroupHeader>
              ),
              children: (
                <RowList>
                  {g.rows.map((row) => (
                    <DiffRowView key={row.path.join('.')} row={row} />
                  ))}
                </RowList>
              ),
            }))}
          />
        </>
      )}
    </Drawer>
  )
}

const DiffRowView: FC<{ row: AssetModelDiffRow }> = ({ row }) => {
  const canHighlight =
    row.status === 'changed' &&
    row.before !== MISSING_VALUE_PLACEHOLDER &&
    row.after !== MISSING_VALUE_PLACEHOLDER
  const highlighted = canHighlight ? diffTokens(row.before, row.after) : null

  return (
    <RowCard>
      <RowHeader>
        <RowLabel>{row.label}</RowLabel>
        <StatusTag status={row.status} />
      </RowHeader>
      <ValueGrid>
        <ValueBlock>
          <ValueHeader>Original (unit)</ValueHeader>
          <ValueBody $missing={row.status === 'added'} $tone={row.status}>
            {highlighted ? (
              <SegmentedText segments={highlighted.llm} side='before' />
            ) : (
              row.before
            )}
          </ValueBody>
        </ValueBlock>
        <ValueBlock>
          <ValueHeader>This ticket</ValueHeader>
          <ValueBody $missing={row.status === 'removed'} $tone={row.status}>
            {highlighted ? (
              <SegmentedText segments={highlighted.user} side='after' />
            ) : (
              row.after
            )}
          </ValueBody>
        </ValueBlock>
      </ValueGrid>
    </RowCard>
  )
}

const STATUS_META: Record<
  AssetModelDiffRow['status'],
  { label: string; color: string }
> = {
  changed: { label: 'Changed', color: 'orange' },
  added: { label: 'Added', color: 'blue' },
  removed: { label: 'Removed', color: 'purple' },
}

const StatusTag: FC<{ status: AssetModelDiffRow['status'] }> = ({ status }) => {
  const meta = STATUS_META[status]
  return <Tag color={meta.color}>{meta.label}</Tag>
}

type Segment = { text: string; kind: 'same' | 'unique' }

const SegmentedText: FC<{ segments: Segment[]; side: 'before' | 'after' }> = ({
  segments,
  side,
}) => (
  <>
    {segments.map((seg, i) =>
      seg.kind === 'unique' ? (
        <TokenMark key={i} $side={side}>
          {seg.text}
        </TokenMark>
      ) : (
        <span key={i}>{seg.text}</span>
      )
    )}
  </>
)

// ---------------------------------------------------------------------------
// Styled components (copied from LlmDiffDrawer's visual conventions)
// ---------------------------------------------------------------------------

const SummaryLine = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 1rem;
`

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const GroupTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const GroupCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const RowCard = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  padding: 0.625rem 0.75rem;
  background: ${({ theme }) => theme.colors.gray[800]};
`

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`

const RowLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.8125rem;
`

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`

const ValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

const ValueHeader = styled.span`
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

type RowStatus = AssetModelDiffRow['status']

const TONE_BORDER: Record<RowStatus, string> = {
  changed: '#faad14',
  added: '#1677ff',
  removed: '#722ed1',
}

const TONE_BG: Record<RowStatus, string> = {
  changed: 'rgba(250, 173, 20, 0.08)',
  added: 'rgba(22, 119, 255, 0.08)',
  removed: 'rgba(114, 46, 209, 0.08)',
}

const ValueBody = styled.div<{ $missing: boolean; $tone: RowStatus }>`
  font-size: 0.8125rem;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  background: ${({ $tone }) => TONE_BG[$tone]};
  border: 1px solid ${({ $tone }) => TONE_BORDER[$tone]};
  color: ${({ $missing, theme }) =>
    $missing ? theme.forecast.secondaryText : theme.forecast.activeText};
  font-style: ${({ $missing }) => ($missing ? 'italic' : 'normal')};
  word-break: break-word;
  white-space: pre-wrap;
`

const TokenMark = styled.span<{ $side: 'before' | 'after' }>`
  background: ${({ $side }) =>
    $side === 'before' ? 'rgba(255, 77, 79, 0.22)' : 'rgba(82, 196, 26, 0.22)'};
  color: ${({ $side, theme }) =>
    $side === 'before' ? '#ff7875' : theme.forecast.activeText};
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 600;
`
